import React, { useContext, useEffect, useState } from "react";
import { Dialog, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import { AuthContext } from "../../context/Auth/AuthContext";

// Global "Authorize" for the API Reference page. The Bearer token entered here
// is kept only in sessionStorage (this browser tab, cleared when it closes) -
// never sent anywhere except as the Authorization header of Try It Out calls.

const STORAGE_KEY = "dmsApiReferenceToken";

export const loadApiToken = () => {
  try {
    return sessionStorage.getItem(STORAGE_KEY) || "";
  } catch (e) {
    return "";
  }
};

export const saveApiToken = (token) => {
  try {
    if (token) sessionStorage.setItem(STORAGE_KEY, token);
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // storage unavailable - token still lives in page state
  }
};

export const maskToken = (token) => (token ? `${token.slice(0, 6)}…${token.slice(-4)}` : "");

// Accepts "Bearer abc" or "abc".
const cleanToken = (value) => value.trim().replace(/^Bearer\s+/i, "").trim();

export const AuthStatusButton = ({ token, onClick }) => (
  <Tooltip title={token ? "Authorized — click to change or clear the token" : "Set a Bearer token for protected APIs"} arrow>
    <button
      type="button"
      onClick={onClick}
      className={`flex h-9 shrink-0 items-center gap-1.5 rounded-md border px-3 text-xs font-semibold ${
        token
          ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
          : "border-gray-300 bg-white text-gray-700 hover:border-nbl-primary hover:text-nbl-primary"
      }`}
    >
      {token ? <LockOutlinedIcon sx={{ fontSize: 16 }} /> : <LockOpenOutlinedIcon sx={{ fontSize: 16 }} />}
      {token ? "Authorized" : "Authorize"}
    </button>
  </Tooltip>
);

const AuthorizeDialog = ({ open, token, onClose, onSave }) => {
  const { token: sessionToken } = useContext(AuthContext);
  const [value, setValue] = useState("");
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (open) {
      setValue("");
      setShow(false);
    }
  }, [open]);

  const authorize = () => {
    const t = cleanToken(value);
    if (!t) return;
    onSave(t);
    onClose();
  };

  const clear = () => {
    onSave("");
    setValue("");
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: "12px" } }}>
      <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-2.5">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-nbl-tint text-nbl-primary">
          <LockOutlinedIcon sx={{ fontSize: 17 }} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-gray-900">Authorize</div>
          <div className="text-[11px] text-gray-500">Bearer token for all protected APIs on this page</div>
        </div>
        <button type="button" onClick={onClose} className="rounded p-1 text-gray-500 hover:bg-gray-100" aria-label="Close">
          <CloseIcon sx={{ fontSize: 18 }} />
        </button>
      </div>

      <div className="flex flex-col gap-3 px-4 py-3">
        <div className="flex items-center gap-2 text-xs">
          <span className="text-gray-500">Status:</span>
          {token ? (
            <span className="rounded border border-emerald-200 bg-emerald-50 px-1.5 py-px font-semibold text-emerald-700">
              Authorized
            </span>
          ) : (
            <span className="rounded border border-gray-200 bg-gray-50 px-1.5 py-px font-semibold text-gray-600">
              Not authorized
            </span>
          )}
          {token && <code className="font-mono text-[11px] text-gray-500">Bearer {maskToken(token)}</code>}
        </div>

        <div>
          <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-500" htmlFor="api-ref-token">
            {token ? "Replace token" : "Bearer token"}
          </label>
          <div className="relative">
            <input
              id="api-ref-token"
              type={show ? "text" : "password"}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && authorize()}
              placeholder="Paste token (with or without “Bearer ”)"
              autoComplete="off"
              spellCheck={false}
              className="h-9 w-full rounded-md border border-gray-300 pl-2 pr-9 font-mono text-xs text-gray-800 focus:border-nbl-primary focus:outline-none"
            />
            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-gray-500 hover:bg-gray-100"
              aria-label={show ? "Hide token" : "Show token"}
            >
              {show ? <VisibilityOffOutlinedIcon sx={{ fontSize: 16 }} /> : <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />}
            </button>
          </div>
          {sessionToken && (
            <button
              type="button"
              onClick={() => setValue(sessionToken)}
              className="mt-1 text-[11px] font-medium text-nbl-primary hover:underline"
            >
              Use my current DMS login token
            </button>
          )}
          <p className="mt-1 text-[11px] leading-snug text-gray-500">
            Kept only for this browser tab (session) and cleared when it closes. Not saved to the database.
          </p>
        </div>

        <div className="flex items-center justify-between gap-2 pt-1">
          <button
            type="button"
            onClick={clear}
            disabled={!token}
            className="flex items-center gap-1 rounded-md border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <LogoutOutlinedIcon sx={{ fontSize: 15 }} /> Logout / Clear Token
          </button>
          <button
            type="button"
            onClick={authorize}
            disabled={!cleanToken(value)}
            className="flex items-center gap-1.5 rounded-md bg-nbl-primary px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <LockOutlinedIcon sx={{ fontSize: 15 }} /> Authorize
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default AuthorizeDialog;
